// Shared, memoized Cognito session/ID-token getters.
//
// Why this exists: every caller that hits the Lambda or Apollo passes a
// `getIdToken` closure into a context, and `useAuthSession` is mounted in
// multiple components. Each call ends up triggering its own
// `fetchAuthSession()`. Amplify v6 only caches *valid* sessions internally,
// but parallel calls race (Apollo `setContext` fires per operation; multiple
// hooks mount concurrently) and Amplify ends up issuing dozens of
// `cognito-identity` GetId / GetCredentialsForIdentity pairs per session
// because the identity-pool credentials are coupled to the ID-token fetch.
//
// This module provides:
//   - In-flight dedupe — concurrent callers share one `fetchAuthSession()`
//     promise.
//   - Token-cache — once we have a valid JWT, hand it back from memory until
//     ~1m before exp. Saves all the redundant Amplify round-trips.
//
// `clearIdTokenCache()` is exported so AuthProvider can drop the cache on
// sign-out / forced refresh.

import { fetchAuthSession } from '@aws-amplify/auth';

type AmplifySession = Awaited<ReturnType<typeof fetchAuthSession>>;

interface CachedEntry {
  session: AmplifySession;
  // Unix-epoch seconds at which we should consider the cache stale and
  // pre-fetch a fresh session.
  refreshAt: number;
}

const REFRESH_SKEW_SEC = 60; // refresh ~1m before exp so callers don't hit a hard failure.

let cached: CachedEntry | null = null;
let inflight: Promise<AmplifySession | null> | null = null;
// Bumped by clearIdTokenCache() so an in-flight fetch started before a
// sign-out can't re-populate `cached` with the old user's session.
let generation = 0;

interface IdTokenClaims {
  exp?: number;
  email?: string;
  sub?: string;
  'cognito:username'?: string;
}

function decodeClaims(jwt: string | undefined): IdTokenClaims | undefined {
  if (!jwt) return undefined;
  const parts = jwt.split('.');
  if (parts.length !== 3 || !parts[1]) return undefined;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as IdTokenClaims;
  } catch {
    return undefined;
  }
}

function decodeExp(jwt: string | undefined): number | undefined {
  return decodeClaims(jwt)?.exp;
}

/**
 * Get the Amplify auth session, hitting `fetchAuthSession()` only when the
 * cached entry is missing or near-expiry. Concurrent callers share the
 * in-flight promise. Returns `null` on failure.
 */
export async function getAuthSession(): Promise<AmplifySession | null> {
  const nowSec = Math.floor(Date.now() / 1000);
  if (cached && cached.refreshAt > nowSec) return cached.session;

  if (inflight) return inflight;

  const startGeneration = generation;
  inflight = (async () => {
    try {
      const session = await fetchAuthSession();
      // The cache was cleared (sign-out / user switch) while this fetch was
      // in flight — don't re-cache the stale session.
      if (generation !== startGeneration) return null;
      const idToken = session.tokens?.idToken?.toString();
      const exp = decodeExp(idToken);
      const refreshAt =
        typeof exp === 'number' ? exp - REFRESH_SKEW_SEC : nowSec + 5 * 60; // fallback: 5 min if exp is unreadable
      cached = { session, refreshAt };
      return session;
    } catch {
      if (generation === startGeneration) cached = null;
      return null;
    } finally {
      if (generation === startGeneration) inflight = null;
    }
  })();

  return inflight;
}

/**
 * Convenience getter that resolves to just the ID-token string. Backed by the
 * same cache as `getAuthSession`.
 */
export async function getIdToken(): Promise<string | undefined> {
  const session = await getAuthSession();
  return session?.tokens?.idToken?.toString();
}

/**
 * Resolve the user's email from the ID-token's `email` claim.
 *
 * Why this exists: Amplify's `getCurrentUser()` returns `username`, which for
 * federated identities (Google OAuth) is `Google_<sub>` — not the email. The
 * ID-token JWT we already cache contains the real email claim from the OAuth
 * provider, so reading it from there is the source of truth.
 */
export async function getUserEmail(): Promise<string | undefined> {
  const session = await getAuthSession();
  const jwt = session?.tokens?.idToken?.toString();
  return decodeClaims(jwt)?.email;
}

/** Drop the in-memory cache. Call on sign-out / user switch / forced refresh. */
export function clearIdTokenCache(): void {
  generation += 1;
  cached = null;
  inflight = null;
}
